<template>
  <Dialog 
    v-model:visible="isVisible" 
    modal 
    :header="title" 
    :style="{ width: '90vw', maxWidth: '900px'}"
    class="recipe-details-dialog dialog-full-screen"
    @hide="$emit('close')"
  >
    <div class="recipe-details">
      <!-- Tabs for different sections -->
      <TabView>
        <!-- Ingredients Tab -->
        <TabPanel header="Ingredients">
          <div v-if="ingredients && ingredients.length > 0" class="ingredients-section">
            <div v-if="originalServings" class="servings-info">
              <Users :size="18" />
              <div class="servings-controls">
                <span class="servings-label"><strong>Servings:</strong></span>
                <InputNumber 
                  v-model="currentServings" 
                  :min="1" 
                  :max="99"
                  showButtons 
                  buttonLayout="horizontal"
                  :step="1"
                  decrementButtonClassName="p-button-outlined p-button-sm"
                  incrementButtonClassName="p-button-outlined p-button-sm"
                  class="servings-stepper"
                />
                <span v-if="isScaled" class="scale-indicator">
                  ({{ (currentServings / originalServings * 100).toFixed(0) }}% of original)
                </span>
              </div>
              <div class="measurement-switcher">
                <button 
                  :class="['measure-btn', { active: measurementSystem === 'metric' }]"
                  @click="measurementSystem = 'metric'"
                >
                  Metric
                </button>
                <button 
                  :class="['measure-btn', { active: measurementSystem === 'us' }]"
                  @click="measurementSystem = 'us'"
                >
                  US
                </button>
              </div>
            </div>
            
            <div class="ingredients-list">
              <div 
                v-for="(ingredient, index) in ingredients" 
                :key="index"
                class="ingredient-item"
              >
                <img 
                  v-if="ingredient.image"
                  :src="`https://spoonacular.com/cdn/ingredients_100x100/${ingredient.image}`"
                  :alt="ingredient.name"
                  class="ingredient-image"
                  @error="onImgError"
                />
                <div class="ingredient-details">
                  <h4 class="ingredient-name">{{ ingredient.name || ingredient.nameClean || ingredient.originalName }}</h4>
                  <p class="ingredient-amount">
                    <template v-if="ingredient.measures && ingredient.measures[measurementSystem]">
                      {{ formatAmount(ingredient.measures[measurementSystem].amount) }} 
                      {{ ingredient.measures[measurementSystem].unitLong || ingredient.measures[measurementSystem].unitShort }}
                    </template>
                    <template v-else>
                      {{ formatAmount(ingredient.amount) }} 
                      {{ ingredient.unit }}
                    </template>
                  </p>
                </div>
                <p v-if="ingredient.original && !isScaled" class="ingredient-original">{{ ingredient.original }}</p>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <Package :size="48" />
            <p>No ingredients information available</p>
          </div>
        </TabPanel>

        <!-- Instructions Tab -->
        <TabPanel header="Instructions">
          <div v-if="instructions && instructions.length > 0" class="instructions-section">
            <div 
              v-for="(instructionSet, setIndex) in instructions" 
              :key="setIndex"
              class="instruction-set"
            >
              <h3 v-if="instructionSet.name" class="instruction-set-name">{{ instructionSet.name }}</h3>
              
              <div 
                v-for="step in instructionSet.steps" 
                :key="step.number"
                class="instruction-step"
              >
                <div class="step-number">{{ step.number }}</div>
                <div class="step-content">
                  <p class="step-text">{{ step.step }}</p>
                  
                  <!-- Step duration -->
                  <div v-if="step.length" class="step-duration">
                    <Clock :size="14" />
                    <span>{{ step.length.number }} {{ step.length.unit }}</span>
                  </div>
                  
                  <!-- Ingredients used in this step -->
                  <div v-if="step.ingredients && step.ingredients.length > 0" class="step-ingredients">
                    <span class="step-section-label">Ingredients:</span>
                    <div class="step-ingredient-tags">
                      <Tag 
                        v-for="ing in step.ingredients" 
                        :key="ing.id"
                        :value="ing.name"
                        class="ingredient-tag"
                      >
                        <template #icon>
                          <img 
                            v-if="ing.image"
                            :src="`https://spoonacular.com/cdn/ingredients_100x100/${ing.image}`"
                            :alt="ing.name"
                            class="tag-image"
                            @error="onImgError"
                          />
                        </template>
                      </Tag>
                    </div>
                  </div>
                  
                  <!-- Equipment needed for this step -->
                  <div v-if="step.equipment && step.equipment.length > 0" class="step-equipment">
                    <span class="step-section-label">Equipment:</span>
                    <div class="step-equipment-tags">
                      <Tag 
                        v-for="equip in step.equipment" 
                        :key="equip.id"
                        :value="equip.name"
                        severity="secondary"
                        class="equipment-tag"
                      >
                        <template #icon>
                          <img 
                            v-if="equip.image"
                            :src="equip.image"
                            :alt="equip.name"
                            class="tag-image"
                            @error="onImgError"
                          />
                        </template>
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <FileText :size="48" />
            <p>No instructions available</p>
          </div>
        </TabPanel>

        <!-- Nutrition Tab -->
        <TabPanel header="Nutrition">
          <div v-if="nutrition" class="nutrition-section">
            <!-- Caloric Breakdown with Pie Chart -->
            <div v-if="nutrition.caloricBreakdown && macroNutrients" class="caloric-breakdown">
              <h3>Caloric Breakdown</h3>
              <div class="breakdown-container">
                <!-- Pie Chart -->
                <div class="pie-chart-container">
                  <svg viewBox="0 0 200 200" class="pie-chart">
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      :stroke="'#f59e0b'"
                      stroke-width="40"
                      :stroke-dasharray="`${nutrition.caloricBreakdown.percentFat * 5.027} 502.7`"
                      transform="rotate(-90 100 100)"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      :stroke="'#3b82f6'"
                      stroke-width="40"
                      :stroke-dasharray="`${nutrition.caloricBreakdown.percentCarbs * 5.027} 502.7`"
                      :stroke-dashoffset="`${-nutrition.caloricBreakdown.percentFat * 5.027}`"
                      transform="rotate(-90 100 100)"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      :stroke="'#10b981'"
                      stroke-width="40"
                      :stroke-dasharray="`${nutrition.caloricBreakdown.percentProtein * 5.027} 502.7`"
                      :stroke-dashoffset="`${-(nutrition.caloricBreakdown.percentFat + nutrition.caloricBreakdown.percentCarbs) * 5.027}`"
                      transform="rotate(-90 100 100)"
                    />
                  </svg>
                </div>
                
                <!-- Legend with absolute values -->
                <div class="breakdown-legend">
                  <div class="legend-item">
                    <div class="legend-color fat"></div>
                    <div class="legend-content">
                      <div class="legend-label">
                        <Droplet :size="14" />
                        <span>Fat</span>
                      </div>
                      <div class="legend-values">
                        <span class="legend-amount">{{ macroNutrients.fat ? formatAmount(macroNutrients.fat.amount) + 'g' : '-' }}</span>
                        <span class="legend-percent">{{ nutrition.caloricBreakdown.percentFat.toFixed(1) }}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="legend-item">
                    <div class="legend-color carbs"></div>
                    <div class="legend-content">
                      <div class="legend-label">
                        <Wheat :size="14" />
                        <span>Carbs</span>
                      </div>
                      <div class="legend-values">
                        <span class="legend-amount">{{ macroNutrients.carbs ? formatAmount(macroNutrients.carbs.amount) + 'g' : '-' }}</span>
                        <span class="legend-percent">{{ nutrition.caloricBreakdown.percentCarbs.toFixed(1) }}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="legend-item">
                    <div class="legend-color protein"></div>
                    <div class="legend-content">
                      <div class="legend-label">
                        <Beef :size="14" />
                        <span>Protein</span>
                      </div>
                      <div class="legend-values">
                        <span class="legend-amount">{{ macroNutrients.protein ? formatAmount(macroNutrients.protein.amount) + 'g' : '-' }}</span>
                        <span class="legend-percent">{{ nutrition.caloricBreakdown.percentProtein.toFixed(1) }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nutrient Legend -->
            <div class="nutrient-legend">
              <div class="legend-bar-sample limit-legend"></div>
              <div class="legend-bar-sample getenough-legend"></div>
              <span class="legend-text">covered percent of daily need</span>
            </div>

            <!-- Limit These Nutrients -->
            <div v-if="limitTheseNutrients.length > 0" class="nutrient-category">
              <h3 class="category-title limit-title">Limit These</h3>
              <div class="nutrient-bars-list">
                <div 
                  v-for="nutrient in limitTheseNutrients" 
                  :key="nutrient.name"
                  class="nutrient-bar-item"
                >
                  <span class="nutrient-bar-name">{{ nutrient.name }}</span>
                  <span class="nutrient-bar-amount">{{ formatAmount(nutrient.amount) }}{{ nutrient.unit }}</span>
                  <div class="nutrient-bar-container">
                    <div 
                      class="nutrient-bar-fill limit-bar"
                      :style="{ width: Math.min(nutrient.percentOfDailyNeeds || 0, 100) + '%' }"
                    ></div>
                  </div>
                  <span class="nutrient-bar-percent">{{ nutrient.percentOfDailyNeeds ? nutrient.percentOfDailyNeeds.toFixed(0) + '%' : '0%' }}</span>
                </div>
              </div>
            </div>

            <!-- Get Enough of These Nutrients -->
            <div v-if="getEnoughNutrients.length > 0" class="nutrient-category">
              <h3 class="category-title getenough-title">Get Enough Of These</h3>
              <div class="nutrient-bars-list">
                <div 
                  v-for="nutrient in getEnoughNutrients" 
                  :key="nutrient.name"
                  class="nutrient-bar-item"
                >
                  <span class="nutrient-bar-name">{{ nutrient.name }}</span>
                  <span class="nutrient-bar-amount">{{ formatAmount(nutrient.amount) }}{{ nutrient.unit }}</span>
                  <div class="nutrient-bar-container">
                    <div 
                      class="nutrient-bar-fill getenough-bar"
                      :style="{ width: Math.min(nutrient.percentOfDailyNeeds || 0, 100) + '%' }"
                    ></div>
                  </div>
                  <span class="nutrient-bar-percent">{{ nutrient.percentOfDailyNeeds ? nutrient.percentOfDailyNeeds.toFixed(0) + '%' : '0%' }}</span>
                </div>
              </div>
            </div>

            <!-- Properties (Glycemic Index, etc.) -->
            <div v-if="nutrition.properties && nutrition.properties.length > 0" class="nutrition-properties">
              <h3>Additional Properties</h3>
              <div class="properties-grid">
                <div 
                  v-for="prop in nutrition.properties" 
                  :key="prop.name"
                  class="property-item"
                >
                  <span class="property-name">{{ prop.name }}</span>
                  <span class="property-value">
                    {{ formatAmount(prop.amount) }}{{ prop.unit }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <Activity :size="48" />
            <p>No nutrition information available</p>
          </div>
        </TabPanel>
      </TabView>
    </div>
  </Dialog>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import Dialog from 'primevue/dialog'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Tag from 'primevue/tag'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import InputNumber from 'primevue/inputnumber'
import { Users, Clock, Package, FileText, Activity, Droplet, Wheat, Beef } from 'lucide-vue-next'

export default {
  name: 'RecipeDetailsModal',
  components: {
    Dialog,
    TabView,
    TabPanel,
    Tag,
    Accordion,
    AccordionTab,
    InputNumber,
    Users,
    Clock,
    Package,
    FileText,
    Activity,
    Droplet,
    Wheat,
    Beef
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: 'Recipe Details'
    },
    metadata: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isVisible = computed({
      get: () => props.visible,
      set: (value) => {
        if (!value) emit('close')
      }
    })
    
    const isMobile = ref(false)
    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 960
    }
    
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', checkMobile)
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
    })

    const originalServings = computed(() => {
      return props.metadata.servings?.content || 1
    })
    
    const currentServings = ref(originalServings.value)
    const measurementSystem = ref('metric') // 'metric' or 'us'
    
    // Watch for changes in metadata to reset servings
    watch(() => props.metadata.servings?.content, (newVal) => {
      if (newVal) {
        currentServings.value = newVal
      }
    })
    
    const servings = computed(() => currentServings.value)
    
    const servingScale = computed(() => {
      return currentServings.value / originalServings.value
    })
    
    const isScaled = computed(() => {
      return servingScale.value !== 1
    })

    const ingredients = computed(() => {
      const baseIngredients = props.metadata.ingredients?.content || []
      if (!isScaled.value) return baseIngredients
      
      // Scale ingredient amounts
      return baseIngredients.map(ing => {
        const scaled = { ...ing }
        if (typeof ing.amount === 'number') {
          scaled.amount = ing.amount * servingScale.value
        }
        if (ing.measures) {
          scaled.measures = {
            metric: ing.measures.metric ? {
              ...ing.measures.metric,
              amount: ing.measures.metric.amount * servingScale.value
            } : undefined,
            us: ing.measures.us ? {
              ...ing.measures.us,
              amount: ing.measures.us.amount * servingScale.value
            } : undefined
          }
        }
        return scaled
      })
    })

    const instructions = computed(() => {
      return props.metadata.instructions?.content || []
    })

    const nutrition = computed(() => {
      return props.metadata.nutrition?.content || null
    })

    // Get macronutrients with absolute amounts
    const macroNutrients = computed(() => {
      if (!nutrition.value?.nutrients) return null
      
      const fat = nutrition.value.nutrients.find(n => n.name === 'Fat')
      const carbs = nutrition.value.nutrients.find(n => n.name === 'Carbohydrates')
      const protein = nutrition.value.nutrients.find(n => n.name === 'Protein')
      
      return { fat, carbs, protein }
    })
    
    // Separate nutrients into categories for better display
    const limitTheseNutrients = computed(() => {
      if (!nutrition.value?.nutrients) return []
      
      const limitList = ['Calories', 'Fat', 'Saturated Fat', 'Carbohydrates', 'Sugar', 'Cholesterol', 'Sodium']
      return nutrition.value.nutrients.filter(n => limitList.includes(n.name))
    })
    
    const getEnoughNutrients = computed(() => {
      if (!nutrition.value?.nutrients) return []
      
      const getEnoughList = ['Protein', 'Vitamin B12', 'Zinc', 'Selenium', 'Phosphorus', 'Vitamin D3', 
                             'Vitamin B6', 'Vitamin B2', 'Manganese', 'Potassium', 'Iron', 'Calcium', 
                             'Fiber', 'Vitamin B1', 'Vitamin E', 'Folate', 'Vitamin B5', 'Copper', 
                             'Magnesium', 'Vitamin K', 'Vitamin C', 'Vitamin A', 'Vitamin D']
      
      return nutrition.value.nutrients
        .filter(n => getEnoughList.includes(n.name) && n.percentOfDailyNeeds > 0)
        .sort((a, b) => b.percentOfDailyNeeds - a.percentOfDailyNeeds)
    })

    const formatAmount = (amount) => {
      if (amount === null || amount === undefined) return ''
      
      // Round to 2 decimal places
      const rounded = Math.round(amount * 100) / 100
      return rounded.toString()
    }

    const onImgError = (e) => {
      e.target.style.display = 'none'
    }

    return {
      isVisible,
      servings,
      currentServings,
      originalServings,
      isScaled,
      measurementSystem,
      ingredients,
      instructions,
      nutrition,
      macroNutrients,
      limitTheseNutrients,
      getEnoughNutrients,
      formatAmount,
      onImgError
    }
  }
}
</script>

<style scoped>
.recipe-details {
  min-height: 400px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-secondary);
  gap: 1rem;
}

/* Ingredients Section */
.ingredients-section {
  padding: 1rem 0;
}

.servings-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.servings-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.servings-label {
  white-space: nowrap;
}

.servings-stepper {
  max-width: 150px;
}

.servings-stepper :deep(.p-inputnumber-input) {
  text-align: center;
  font-weight: 600;
  width: 60px;
}

.servings-stepper :deep(.p-inputnumber-button) {
  width: 2rem;
}

.scale-indicator {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-style: italic;
}

.measurement-switcher {
  display: flex;
  gap: 0.25rem;
  background: var(--bg-primary);
  padding: 0.25rem;
  border-radius: 6px;
}

.measure-btn {
  padding: 0.4rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.measure-btn:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.measure-btn.active {
  background: #00843c;
  color: white;
}

.ingredients-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ingredient-item {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  transition: all 0.2s ease;
}

.ingredient-item:last-child {
  border-bottom: none;
}

.ingredient-item:hover {
  padding-left: 0.5rem;
  background: var(--bg-primary);
  margin-left: -0.5rem;
  margin-right: -0.5rem;
  padding-right: 0.5rem;
  border-radius: 4px;
}

.ingredient-image {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: contain;
  background: var(--bg-secondary);
  padding: 4px;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.ingredient-details {
  flex: 1;
  min-width: 0;
}

.ingredient-name {
  margin: 0 0 0.25rem 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: capitalize;
}

.ingredient-amount {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.ingredient-original {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: var(--text-tertiary);
  font-style: italic;
}

/* Instructions Section */
.instructions-section {
  padding: 1rem 0;
}

.instruction-set {
  margin-bottom: 2rem;
}

.instruction-set-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
}

.instruction-step {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.step-number {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #00843c;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.9rem;
}

.step-content {
  flex: 1;
}

.step-text {
  margin: 0 0 0.75rem 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--text-primary);
}

.step-duration {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
}

.step-section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 0.5rem;
}

.step-ingredient-tags,
.step-equipment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ingredient-tag,
.equipment-tag {
  font-size: 0.8rem;
}

.tag-image {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-right: 0.25rem;
}

.step-ingredients,
.step-equipment {
  margin-top: 0.75rem;
}

/* Nutrition Section */
.nutrition-section {
  padding: 1rem 0;
}

.nutrition-section h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
}

/* Caloric Breakdown */
.caloric-breakdown {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.breakdown-container {
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;
}

.pie-chart-container {
  flex-shrink: 0;
  width: 200px;
  height: 200px;
}

.pie-chart {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.breakdown-legend {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-color.fat {
  background: #f59e0b;
}

.legend-color.carbs {
  background: #3b82f6;
}

.legend-color.protein {
  background: #10b981;
}

.legend-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.legend-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.legend-values {
  display: flex;
  gap: 0.75rem;
  align-items: baseline;
}

.legend-amount {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 1rem;
}

.legend-percent {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

/* Nutrient Legend */
.nutrient-legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 0;
}

.legend-bar-sample {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.limit-legend {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

.getenough-legend {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.legend-text {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* Nutrient Categories */
.nutrient-category {
  margin-bottom: 1.5rem;
}

.category-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--border-color);
}

.limit-title {
  color: #dc2626 !important;
}

.getenough-title {
  color: #10b981 !important;
}

.nutrient-bars-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nutrient-bar-item {
  display: grid;
  grid-template-columns: 140px 80px 1fr 60px;
  gap: 0.75rem;
  align-items: center;
  padding: 0.5rem 0;
}

.nutrient-bar-header {
  display: flex;
  align-items: center;
}

.nutrient-bar-name {
  font-weight: 400;
  font-size: 0.9rem;
  color: var(--text-primary);
}

.nutrient-bar-amount {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-secondary);
  white-space: nowrap;
  text-align: right;
}

.nutrient-bar-container {
  height: 10px;
  background: var(--border-color);
  border-radius: 5px;
  overflow: hidden;
  flex: 1;
}

.nutrient-bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.5s ease;
}

.nutrient-bar-fill.limit-bar {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

.nutrient-bar-fill.getenough-bar {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.nutrient-bar-percent {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-align: right;
}

/* Properties */
.nutrition-properties {
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.property-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.property-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.property-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
}

/* TabView active indicator - green color */
:deep(.p-tabview-nav-link:focus) {
  box-shadow: inset 0 0 0 0.2rem rgba(0, 132, 60, 0.2);
}

:deep(.p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  border-color: #00843c;
  color: #00843c;
}

</style>

